import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Drawer,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { getTemplateStyles, formatDiscountBadge } from '../config/templateStyles';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

function ProductList() {
  const { t } = useTranslation('product');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const SORT_OPTIONS = useMemo(() => [
    { value: 'created_at', label: t('list.sort_newest'), order: 'DESC' },
    { value: 'sold_count', label: t('list.sort_best_selling'), order: 'DESC' },
    { value: 'sale_price', label: t('list.sort_price_asc'), order: 'ASC' },
  ], [t]);

  const { addToCart } = useCart();
  const { templateId } = useStore();
  const tplStyles = getTemplateStyles(templateId);
  const { trackSearch, trackCategory } = useBehaviorTracking();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const lastSearchRef = useRef('');

  const [filters, setFilters] = useState(() => ({
    search: searchParams.get('search') || '',
    categoryId: searchParams.get('category') || '',
    sort: searchParams.get('sort') || 'created_at',
    page: Number(searchParams.get('page') || 1) || 1,
  }));

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlCategory = searchParams.get('category') || '';
    const urlSort = searchParams.get('sort') || 'created_at';
    const urlPage = Number(searchParams.get('page') || 1) || 1;
    setFilters((prev) => {
      if (
        prev.search === urlSearch &&
        prev.categoryId === urlCategory &&
        prev.sort === urlSort &&
        prev.page === urlPage
      ) {
        return prev;
      }
      return { search: urlSearch, categoryId: urlCategory, sort: urlSort, page: urlPage };
    });
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/products/categories');
        setCategories(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setCategories([]);
      }
    })();
  }, []);

  const selectedCategoryName = useMemo(() => {
    if (!filters.categoryId) return t('list.category_all');
    const id = Number(filters.categoryId);
    const c = categories.find((x) => x.id === id);
    return c?.name || t('list.category');
  }, [filters.categoryId, categories]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const sortOpt = SORT_OPTIONS.find((s) => s.value === filters.sort) || SORT_OPTIONS[0];
      const params = {
        page: filters.page,
        limit: 20,
        sort: sortOpt.value,
        order: sortOpt.order,
      };
      if (filters.search) params.search = filters.search;
      if (filters.categoryId) params.category = filters.categoryId; // numeric ok

      const res = await api.get('/products', { params });
      setProducts(res.data?.items || []);
      setPagination(res.data?.pagination || { page: 1, totalPages: 1, total: 0 });

      if (filters.search && filters.search !== lastSearchRef.current) {
        trackSearch(filters.search, res.data?.pagination?.total || 0);
        lastSearchRef.current = filters.search;
      }
      if (filters.categoryId) trackCategory(filters.categoryId);

      // GA4: view_item_list
      if (window.dataLayer && Array.isArray(res.data?.items) && res.data.items.length > 0) {
        const listName = filters.search ? `${t('list.search_results')}: ${filters.search}` : selectedCategoryName;
        const listId = filters.search ? `search_${filters.search}` : filters.categoryId ? `category_${filters.categoryId}` : 'all_products';
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_item_list',
          ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items: res.data.items.slice(0, 20).map((p, idx) => ({
              item_id: p.sku || String(p.id),
              item_name: p.name,
              item_category: p.category_name || '',
              price: Number(p.sale_price),
              index: idx,
              quantity: 1,
            })),
          },
        });
      }
    } catch (e) {
      setProducts([]);
      setPagination({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.categoryId) params.set('category', String(filters.categoryId));
    if (filters.sort !== 'created_at') params.set('sort', filters.sort);
    if (filters.page > 1) params.set('page', String(filters.page));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.categoryId, filters.sort, filters.page]);

  const discountPct = (original, sale) => {
    const o = Number(original);
    const s = Number(sale);
    if (!o || !s || o <= 0) return 0;
    const pct = Math.round((1 - s / o) * 100);
    return Number.isFinite(pct) ? Math.max(0, pct) : 0;
  };

  const FilterPanel = () => (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={900} sx={{ mb: 1 }}>
        {t('list.category')}
      </Typography>
      <Stack spacing={0.5}>
        <Button
          variant={!filters.categoryId ? 'contained' : 'text'}
          onClick={() => setFilters((p) => ({ ...p, categoryId: '', page: 1 }))}
          sx={{ justifyContent: 'flex-start', borderRadius: 2, fontWeight: 900 }}
        >
          {t('list.category_all')}
        </Button>
        {categories.map((c) => (
          <Button
            key={c.id}
            variant={String(filters.categoryId) === String(c.id) ? 'contained' : 'text'}
            onClick={() => setFilters((p) => ({ ...p, categoryId: String(c.id), page: 1 }))}
            sx={{ justifyContent: 'flex-start', borderRadius: 2, fontWeight: 900 }}
          >
            {c.name}
          </Button>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography fontWeight={900} sx={{ mb: 1 }}>
        {t('list.sort')}
      </Typography>
      <FormControl fullWidth size="small">
        <Select value={filters.sort} onChange={(e) => setFilters((p) => ({ ...p, sort: e.target.value, page: 1 }))}>
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Paper sx={{ p: 2, borderRadius: tplStyles.card.borderRadius, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            {isMobile && (
              <Button startIcon={<FilterListIcon />} variant="outlined" onClick={() => setFilterOpen(true)}>
                {t('list.filter')}
              </Button>
            )}

            <TextField
              size="small"
              placeholder={t('list.search_placeholder')}
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value, page: 1 }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 240 }}
            />

            {!isMobile && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>{t('list.sort')}</InputLabel>
                <Select
                  value={filters.sort}
                  label={t('list.sort')}
                  onChange={(e) => setFilters((p) => ({ ...p, sort: e.target.value, page: 1 }))}
                >
                  {SORT_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
              {t('list.total_count', { count: pagination.total || 0 })}
            </Typography>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {!!filters.categoryId && (
            <Chip label={`${t('list.category')}: ${selectedCategoryName}`} onDelete={() => setFilters((p) => ({ ...p, categoryId: '', page: 1 }))} />
          )}
          {!!filters.search && (
            <Chip label={`${t('list.search_results')}: ${filters.search}`} onDelete={() => setFilters((p) => ({ ...p, search: '', page: 1 }))} />
          )}
        </Stack>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isMobile && (
            <Paper sx={{ width: 280, borderRadius: tplStyles.card.borderRadius, position: 'sticky', top: 100, height: 'fit-content' }}>
              <FilterPanel />
            </Paper>
          )}

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 2,
              }}
            >
              {loading
                ? Array.from({ length: 10 }).map((_, i) => (
                  <Card key={i} sx={{ borderRadius: tplStyles.card.borderRadius }}>
                    <Skeleton variant="rectangular" height={180} />
                    <CardContent>
                      <Skeleton width="80%" />
                      <Skeleton width="60%" />
                      <Skeleton width="40%" />
                    </CardContent>
                  </Card>
                ))
                : products.map((p, idx) => {
                  const pct = discountPct(p.original_price, p.sale_price);
                  const badgeText = formatDiscountBadge(templateId, pct);
                  return (
                    <Card
                      key={p.id}
                      sx={{
                        borderRadius: tplStyles.card.borderRadius,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(0,0,0,0.08)' },
                      }}
                    >
                      <Box sx={{ position: 'relative', bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fafafa' }}>
                        {badgeText && (
                          <Chip
                            label={badgeText}
                            color="error"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              fontWeight: 900,
                              zIndex: 1,
                              borderRadius: tplStyles.badge.borderRadius,
                            }}
                          />
                        )}
                        <CardMedia
                          component="img"
                          height="180"
                          image={normalizeImageUrl(p.image_url, p.name)}
                          alt={p.name}
                          sx={{ objectFit: 'contain', p: 1.5, cursor: 'pointer' }}
                          onError={(e) => onImageErrorSetFallback(e, p.name)}
                          onClick={() => {
                            if (window.dataLayer) {
                              window.dataLayer.push({ ecommerce: null });
                              window.dataLayer.push({
                                event: 'select_item',
                                ecommerce: {
                                  items: [
                                    {
                                      item_id: p.sku || String(p.id),
                                      item_name: p.name,
                                      item_category: p.category_name || '',
                                      price: Number(p.sale_price),
                                      index: idx,
                                      quantity: 1,
                                    },
                                  ],
                                },
                              });
                            }
                            navigate(`/products/${p.id}`);
                          }}
                        />
                      </Box>

                      <CardContent sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 900,
                            lineHeight: 1.35,
                            minHeight: 40,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' },
                          }}
                          onClick={() => navigate(`/products/${p.id}`)}
                        >
                          {p.name}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography sx={{ fontWeight: 950, color: 'error.main', fontSize: '1.05rem' }}>
                            ${Number(p.sale_price).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                            ${Number(p.original_price || p.sale_price).toLocaleString()}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} alignItems="center">
                          <Chip
                            size="small"
                            icon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
                            label={t('list.fast_shipping')}
                            sx={{ height: 24, fontWeight: 800, bgcolor: theme.palette.custom?.hoverTint || '#e3f2fd' }}
                          />
                        </Stack>

                        <Button
                          variant={tplStyles.button.variant}
                          startIcon={<ShoppingCartIcon />}
                          disabled={Number(p.quantity) <= 0}
                          onClick={() => addToCart(p.id, 1)}
                          sx={{
                            mt: 1,
                            borderRadius: tplStyles.button.borderRadius,
                            textTransform: tplStyles.button.textTransform,
                            fontWeight: 950,
                          }}
                        >
                          {Number(p.quantity) > 0 ? tplStyles.button.addToCartText : t('detail.out_of_stock')}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
            </Box>

            {!loading && products.length === 0 && (
              <Paper sx={{ mt: 2, p: 6, textAlign: 'center', borderRadius: tplStyles.card.borderRadius }}>
                <Inventory2Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography fontWeight={900} sx={{ mb: 1 }}>
                  {t('list.no_products')}
                </Typography>
                <Typography color="text.secondary">{t('list.no_products_hint')}</Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => setFilters({ search: '', categoryId: '', sort: 'created_at', page: 1 })}>
                  {t('list.clear_filter')}
                </Button>
              </Paper>
            )}

            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={filters.page}
                  onChange={(_, p) => setFilters((prev) => ({ ...prev, page: p }))}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      <Drawer anchor="left" open={filterOpen} onClose={() => setFilterOpen(false)} PaperProps={{ sx: { width: 300 } }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={900}>{t('list.filter')}</Typography>
          <Button color="inherit" startIcon={<CloseIcon />} onClick={() => setFilterOpen(false)} sx={{ fontWeight: 900 }}>
            {t('list.close')}
          </Button>
        </Box>
        <FilterPanel />
      </Drawer>
    </Box>
  );
}

export default ProductList;



